import {
	BrowserMultiFormatOneDReader,
	type IScannerControls,
} from '@zxing/browser'
import { Keyboard, ScanBarcode } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { useTranslate } from 'shared/lib/i18n'
import { playScanSuccessSound } from './scanner-audio'

type CaptureMode = 'scanner' | 'manual'

type TelegramWebApp = {
	HapticFeedback?: {
		notificationOccurred?: (type: 'success' | 'warning' | 'error') => void
	}
}

function ScannerPreview({
	onScan,
	onUnavailable,
}: {
	onScan: (value: string) => void
	onUnavailable: (message: string) => void
}) {
	const { prefixT } = useTranslate('pages.adminTrackCode')
	const videoRef = useRef<HTMLVideoElement>(null)
	const controlsRef = useRef<IScannerControls | null>(null)
	const resultHandledRef = useRef(false)
	const onScanRef = useRef(onScan)
	const onUnavailableRef = useRef(onUnavailable)

	useEffect(() => {
		onScanRef.current = onScan
		onUnavailableRef.current = onUnavailable
	}, [onScan, onUnavailable])

	useEffect(() => {
		const video = videoRef.current
		if (!video) return
		let active = true
		const reader = new BrowserMultiFormatOneDReader()
		resultHandledRef.current = false

		void reader
			.decodeFromConstraints(
				{
					audio: false,
					video: {
						facingMode: { ideal: 'environment' },
						width: { ideal: 1280 },
						height: { ideal: 720 },
					},
				},
				video,
				(result, _error, callbackControls) => {
					if (!result || resultHandledRef.current) return
					const value = result.getText().trim()
					if (!value) return

					// Set this before any async/UI work: ZXing can emit the same frame again.
					resultHandledRef.current = true
					callbackControls.stop()
					controlsRef.current = null
					playScanSuccessSound()
					;(window as Window & { Telegram?: { WebApp?: TelegramWebApp } })
						.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success')
					onScanRef.current(value)
				},
			)
			.then((controls) => {
				if (!active || resultHandledRef.current) {
					controls.stop()
					return
				}
				controlsRef.current = controls
			})
			.catch((error: unknown) => {
				if (!active) return
				const message = error instanceof DOMException && error.name === 'NotAllowedError'
					? prefixT('cameraPermissionDenied')
					: error instanceof DOMException && error.name === 'NotFoundError'
						? prefixT('cameraNotFound')
						: prefixT('cameraOpenFailed')
				onUnavailableRef.current(message)
			})

		return () => {
			active = false
			controlsRef.current?.stop()
			controlsRef.current = null
		}
	}, [prefixT])

	return (
		<div className='overflow-hidden rounded-xl bg-black'>
			<video ref={videoRef} autoPlay playsInline muted className='aspect-[4/3] w-full object-cover' />
		</div>
	)
}

export function TrackCodeCaptureModal({
	open,
	mode,
	initialValue = '',
	onOpenChange,
	onManualEntry,
	onSubmit,
}: {
	open: boolean
	mode: CaptureMode
	initialValue?: string
	onOpenChange: (open: boolean) => void
	onManualEntry: (message?: string) => void
	onSubmit: (value: string) => void
}) {
	const { prefixT } = useTranslate('pages.adminTrackCode')
	const [manualValue, setManualValue] = useState(initialValue)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (open && mode === 'manual') requestAnimationFrame(() => inputRef.current?.focus())
	}, [mode, open])

	const submitManualValue = () => {
		const value = manualValue.trim()
		if (!value) return
		onSubmit(value)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-md'>
				<DialogHeader>
					<DialogTitle>
						{mode === 'scanner' ? prefixT('scanTitle') : prefixT('title')}
					</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-4 pt-2'>
					{mode === 'scanner' ? (
						<>
							<ScannerPreview
								onScan={onSubmit}
								onUnavailable={onManualEntry}
							/>
							<p className='text-center text-sm text-muted-foreground'>
								{prefixT('scanHint')}
							</p>
							<Button
								variant='outline'
								className='w-full'
								onClick={() => onManualEntry()}
							>
								<Keyboard className='size-5' />
								{prefixT('manualEntry')}
							</Button>
						</>
					) : (
						<>
							<Input
								ref={inputRef}
								placeholder={prefixT('example')}
								value={manualValue}
								onChange={(event) => setManualValue(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') submitManualValue()
								}}
							/>
							<Button
								disabled={!manualValue.trim()}
								className='w-full'
								onClick={submitManualValue}
							>
								<ScanBarcode className='size-5' />
								{prefixT('saveTrackCode')}
							</Button>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
